import React from "react";
import { Camera, X, Eye, CheckCircle2, Trash2, Calendar, User, Gauge, FileDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Priority badge helper
const getPriorityBadgeStyles = (priority) => {
  switch (priority) {
    case 'high': return 'bg-red-100 text-red-800';
    case 'medium': return 'bg-yellow-100 text-yellow-800';
    case 'low': return 'bg-green-100 text-green-800';
    default: return 'bg-slate-100 text-slate-800';
  }
};

// Status badge helper
const getStatusBadgeStyles = (status) => {
  switch (status) {
    case 'submitted': return 'bg-blue-100 text-blue-800';
    case 'viewed': return 'bg-purple-100 text-purple-800';
    case 'fulfilled': return 'bg-green-100 text-green-800';
    case 'archived': return 'bg-slate-200 text-slate-600';
    default: return 'bg-slate-100 text-slate-800';
  }
};

// Active Request Card
export const ActiveRequestCard = ({ 
  request, 
  updatingStatus, 
  onStatusUpdate, 
  onDelete, 
  onImageClick,
  canMarkViewed = true,
  canMarkFulfilled = true,
  canReopen = true,
  canDelete = true
}) => {
  return (
    <div 
      className="p-3 sm:p-4" 
      data-testid={`request-${request.id}`}
    >
      <div className="flex flex-col gap-3">
        {/* Header Row: Unit + Priority + Category */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <span className="font-semibold text-slate-900 text-sm sm:text-base">Unit {request.unit_number}</span>
          <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium uppercase ${getPriorityBadgeStyles(request.priority)}`}>
            {request.priority}
          </span>
          <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium ${getStatusBadgeStyles(request.status)}`}>
            {request.status === 'submitted' ? 'NEW' : 'REVIEWED'}
          </span>
          <span className="text-xs sm:text-sm text-slate-500 font-medium capitalize">{request.category}</span>
        </div>
        
        {/* Reviewed By Info - Show when status is viewed */}
        {request.status === 'viewed' && request.reviewed_by && (
          <div className="flex items-center gap-2 text-xs bg-purple-50 text-purple-700 px-2 py-1.5 rounded-lg">
            <Eye className="w-3 h-3" />
            <span>
              Reviewed by <strong>{request.reviewed_by}</strong>
              {request.reviewed_at && (
                <span className="text-purple-500 ml-1">
                  • {new Date(request.reviewed_at).toLocaleString()}
                </span>
              )}
            </span>
          </div>
        )}
        
        {/* Description */}
        <p className="text-xs sm:text-sm text-slate-600 break-words">{request.notes}</p>
        
        {/* Images Preview */}
        {request.images && request.images.length > 0 && (
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
            {request.images.slice(0, 4).map((img, idx) => (
              <div 
                key={idx} 
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0 cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => onImageClick(img)}
              >
                <img src={img} alt={`Issue ${idx + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
            {request.images.length > 4 && (
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] sm:text-xs text-slate-500">+{request.images.length - 4}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Metadata Row */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            {new Date(request.created_at).toLocaleDateString()}
          </span>
          {request.mileage && (
            <span className="flex items-center gap-1">
              <Gauge className="w-3 h-3 flex-shrink-0" />
              {request.mileage.toLocaleString()} mi
            </span>
          )}
          {request.hours && (
            <span className="flex items-center gap-1">
              <span className="text-[10px] sm:text-xs">⏱</span>
              {request.hours.toLocaleString()} hrs
            </span>
          )}
          {request.requested_by && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3 flex-shrink-0" />
              <span className="truncate max-w-[60px] sm:max-w-none">{request.requested_by}</span>
            </span>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          {request.status === 'submitted' && canMarkViewed && (
            <Button
              size="sm"
              onClick={() => onStatusUpdate(request.id, 'viewed')}
              disabled={updatingStatus === request.id}
              className="flex-1 sm:flex-none h-9 text-xs sm:text-sm bg-purple-600 hover:bg-purple-700 text-white"
              data-testid={`btn-mark-viewed-${request.id}`}
            >
              <Eye className="w-3 h-3 mr-1" />
              Review
            </Button>
          )}
          {request.status === 'viewed' && (
            <>
              {canMarkFulfilled && (
                <Button
                  size="sm"
                  onClick={() => onStatusUpdate(request.id, 'fulfilled')}
                  disabled={updatingStatus === request.id}
                  className="flex-1 sm:flex-none h-9 text-xs sm:text-sm bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`btn-complete-${request.id}`}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Complete
                </Button>
              )}
              {canReopen && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onStatusUpdate(request.id, 'submitted')}
                  disabled={updatingStatus === request.id}
                  className="h-9 text-xs sm:text-sm border-orange-300 text-orange-600 hover:bg-orange-50"
                  data-testid={`btn-undo-review-${request.id}`}
                >
                  Undo Review
                </Button>
              )}
            </>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(request.id)}
              className="h-9 text-xs sm:text-sm text-red-500 hover:text-red-600 hover:bg-red-50"
              data-testid={`btn-delete-${request.id}`}
            >
              <Trash2 className="w-3 h-3 sm:mr-1" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Fulfilled Request Card
export const FulfilledRequestCard = ({ 
  request, 
  updatingStatus, 
  onStatusUpdate, 
  onDelete, 
  onImageClick,
  canReopen = true,
  canArchive = true,
  canDelete = true
}) => {
  return (
    <div 
      className="p-3 sm:p-4 bg-green-50/50"
      data-testid={`request-fulfilled-${request.id}`}
    >
      <div className="flex flex-col gap-3">
        {/* Header: Unit + Status badges */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <span className="font-semibold text-slate-900 text-sm sm:text-base">Unit {request.unit_number}</span>
          <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-800">
            COMPLETED
          </span>
          <span className="text-xs sm:text-sm text-slate-500 font-medium capitalize">{request.category}</span>
        </div>
        
        {/* Original request description */}
        <p className="text-xs sm:text-sm text-slate-600">{request.notes}</p>
        
        {/* Metadata row */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            {new Date(request.created_at).toLocaleDateString()}
          </span>
          {request.fulfilled_at && (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
              <span className="hidden xs:inline">Completed</span> {new Date(request.fulfilled_at).toLocaleDateString()}
            </span>
          )}
          {request.completed_by && (
            <span className="flex items-center gap-1 text-blue-600">
              <User className="w-3 h-3 flex-shrink-0" />
              <span className="truncate max-w-[80px] sm:max-w-none">by {request.completed_by}</span>
            </span>
          )}
          {request.mileage && (
            <span className="flex items-center gap-1">
              <Gauge className="w-3 h-3 flex-shrink-0" />
              {request.mileage.toLocaleString()} mi
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {canReopen && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onStatusUpdate(request.id, 'submitted')}
              disabled={updatingStatus === request.id}
              className="flex-1 sm:flex-none h-8 text-xs sm:text-sm text-orange-600 border-orange-300 hover:bg-orange-50"
              data-testid={`btn-reactivate-${request.id}`}
            >
              Reactivate
            </Button>
          )}
          {canArchive && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onStatusUpdate(request.id, 'archived')}
              disabled={updatingStatus === request.id}
              className="flex-1 sm:flex-none h-8 text-xs sm:text-sm text-slate-600 border-slate-300 hover:bg-slate-100"
              data-testid={`btn-archive-${request.id}`}
            >
              <FileDown className="w-3 h-3 sm:mr-1" />
              <span className="hidden sm:inline">Archive</span>
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(request.id)}
              className="h-8 text-xs sm:text-sm text-red-500 hover:text-red-600 hover:bg-red-50"
              data-testid={`btn-delete-fulfilled-${request.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        
        {/* Completion Notes Section */}
        {request.completion_notes && (
          <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-green-200">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-green-700 mb-1">Completion Notes</p>
                <p className="text-xs sm:text-sm text-slate-700 break-words">{request.completion_notes}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Completion Images Section */}
        {request.completion_images && request.completion_images.length > 0 && (
          <div>
            <p className="text-[10px] sm:text-xs font-medium text-slate-500 mb-2">Completion Photos ({request.completion_images.length})</p>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 sm:gap-2">
              {request.completion_images.map((img, idx) => (
                <div 
                  key={idx} 
                  className="aspect-square rounded-lg overflow-hidden border border-slate-200 cursor-pointer hover:border-green-400 transition-colors"
                  onClick={() => onImageClick(img)}
                >
                  <img 
                    src={img} 
                    alt={`Completion ${idx + 1}`} 
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Archived Request Card
export const ArchivedRequestCard = ({ request, onDelete, canDelete = true }) => {
  return (
    <div 
      className="p-4 opacity-75 hover:opacity-100"
      data-testid={`request-archived-${request.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-medium text-slate-700">Unit {request.unit_number}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">ARCHIVED</span>
            <span className="text-sm text-slate-500">{request.category}</span>
          </div>
          <p className="text-sm text-slate-500 line-clamp-1">{request.notes}</p>
          <div className="text-xs text-slate-400 mt-1">
            Archived {request.archived_at ? new Date(request.archived_at).toLocaleDateString() : ''}
          </div>
        </div>
        {canDelete && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(request.id)}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
            data-testid={`btn-delete-archived-${request.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Collapsible Section for Fulfilled/Archived
export const CollapsibleRequestSection = ({
  title,
  count,
  isOpen,
  onToggle,
  dotColor,
  testId,
  children
}) => {
  return (
    <div className={`bg-${dotColor === 'green' ? 'white' : 'slate-50'} rounded-xl border border-slate-200 mb-4`}>
      <button
        onClick={onToggle}
        className={`w-full p-4 flex items-center justify-between hover:bg-${dotColor === 'green' ? 'slate-50' : 'slate-100'} transition-colors`}
        data-testid={testId}
      >
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 bg-${dotColor}-500 rounded-full`}></div>
          <h3 className={`font-${dotColor === 'green' ? 'semibold' : 'medium'} text-slate-${dotColor === 'green' ? '900' : '600'}`}>{title}</h3>
          <span className={`text-xs sm:text-sm text-slate-${dotColor === 'green' ? '500' : '400'}`}>({count})</span>
        </div>
        <ChevronRight className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="border-t border-slate-200 divide-y divide-slate-200">
          {children}
        </div>
      )}
    </div>
  );
};

// Stats Card
export const StatsCard = ({ value, label, accentColor = "blue", badge = null }) => {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-1">
        <span className={`text-xl sm:text-2xl font-bold text-${accentColor}-600`}>{value}</span>
        {badge}
      </div>
      <p className="text-[10px] sm:text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
};
